/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import { LangURLParams, ExternalSocialLinkErrorURLParams, SocialLinkingURLParams } from './types';

const buildRoute = (...routes: string[]): string => routes.join('/');

// region Common Routes
/**
 * In those routes we are using an optional url param instead of required one.
 * It is done to prevent Not Found reaction on urls without specified lang.
 * In case of localization, user can open urls directly without specified lang.
 */

/**
 * Route: /:lang?
 */
export const LangRoot = {
  route: '/:lang?',
  url: ({ lang }: LangURLParams): string => buildRoute('', lang)
};
// end region

// region Account Routers
/**
 * Route: /:lang?/account
 */
export const Account = {
  route: buildRoute(LangRoot.route, 'account'),
  url: ({ lang }: LangURLParams): string => buildRoute(LangRoot.url({ lang }), 'account')
};

/**
 * Route: /:lang?/account/linked-accounts
 */
export const LinkedAccounts = {
  route: buildRoute(Account.route, 'linked-accounts'),
  url: ({ lang }: LangURLParams): string => buildRoute(Account.url({ lang }), 'linked-accounts')
};

/**
 * Route: /:lang?/account/new
 */
export const AccountCreationHandler = {
  route: buildRoute(Account.route, 'new'),
  url: ({ lang }: LangURLParams): string => buildRoute(Account.url({ lang }), 'new')
};

/**
 * Route: /:lang?/account/not-found
 */
export const NotFound = {
  route: buildRoute(Account.route, 'not-found'),
  url: ({ lang }: LangURLParams): string => buildRoute(Account.url({ lang }), 'not-found')
};

/**
 * Route: /:lang?/account/error
 */
export const CommonError = {
  route: buildRoute(Account.route, 'error'),
  url: ({ lang }: LangURLParams): string => buildRoute(Account.url({ lang }), 'error')
};
// end region

// region Auth Routers
/**
 * Route: /:lang?/account/oauth2
 */
export const Auth = {
  route: buildRoute(Account.route, 'oauth2'),
  url: ({ lang }: LangURLParams): string => buildRoute(Account.url({ lang }), 'oauth2')
};

/**
 * Route: /:lang?/account/oauth2/code
 */
export const AuthHandler = {
  route: buildRoute(Auth.route, 'code'),
  url: ({ lang }: LangURLParams): string => buildRoute(Auth.url({ lang }), 'code')
};
// end region

// region Social Routers
/**
 * Route: /:lang?/social
 */
export const Social = {
  route: buildRoute(LangRoot.route, 'social'),
  url: ({ lang }: LangURLParams): string => buildRoute(LangRoot.url({ lang }), 'social')
};

/**
 * Route: /:lang?/social/linking
 */
export const SocialLinking = {
  route: buildRoute(Social.route, 'linking'),
  url: ({ lang }: LangURLParams): string => buildRoute(Social.url({ lang }), 'linking')
};

/**
 * Route: /:lang?/social/linking/amazon
 */
export const SocialAmazonLinking = {
  route: buildRoute(SocialLinking.route, 'amazon'),
  url: ({ lang }: LangURLParams): string => buildRoute(SocialLinking.url({ lang }), 'amazon')
};

/**
 * Route: /:lang?/social/linking/steam
 */
export const SocialSteamLinking = {
  route: buildRoute(SocialLinking.route, 'steam'),
  url: ({ lang }: LangURLParams): string => buildRoute(SocialLinking.url({ lang }), 'steam')
};

/**
 * Route: /:lang?/social/linking/:linkingId/callback
 */
export const SocialLinkingCallback = {
  route: buildRoute(SocialLinking.route, ':linkingId', 'callback'),
  url: ({ lang, linkingId }: SocialLinkingURLParams): string =>
    buildRoute(SocialLinking.url({ lang }), linkingId, 'callback')
};

/**
 * Route: /:lang?/social/linking/success
 */
export const SocialLinkingSuccess = {
  route: buildRoute(SocialLinking.route, 'success'),
  url: ({ lang }: LangURLParams): string => buildRoute(SocialLinking.url({ lang }), 'success')
};

/**
 * Route: /:lang?/social/linking/error
 */
export const SocialLinkingErrorCallback = {
  route: buildRoute(SocialLinking.route, 'error'),
  url: ({ lang }: LangURLParams): string => buildRoute(SocialLinking.url({ lang }), 'error')
};

/**
 * Route: /:lang?/social/linking/error/:provider
 */
export const ExternalSocialLinkingErrorCallback = {
  route: buildRoute(SocialLinkingErrorCallback.route, ':provider'),
  url: ({ lang, provider }: ExternalSocialLinkErrorURLParams): string =>
    buildRoute(SocialLinkingErrorCallback.url({ lang }), provider)
};
// end region
